import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { getNeedsAttentionRides } from "api/dispatchApi";
import { unwrapList } from "api/unwrap";

/**
 * /dispatch/needs-attention — rides stuck in DECLINED / TIMED_OUT / UNMATCHED.
 * These are the ones the auto-reassign loop bounced back to a human. Dispatch
 * uses this view to manually reassign or cancel.
 */

const STATUS_COLORS = {
  DECLINED: "#dc2626",
  TIMED_OUT: "#f97316",
  UNMATCHED: "#a855f7",
};

const STATUS_LABELS = {
  DECLINED: "Declined",
  TIMED_OUT: "Timed out",
  UNMATCHED: "Unmatched",
};

const fmtAge = (ms) => {
  if (!ms) return "—";
  const diff = Date.now() - ms;
  if (diff < 0) return "—";
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const fmtStart = (ms) => {
  if (!ms) return "—";
  const d = new Date(ms);
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const NeedsAttentionPage = () => {
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getNeedsAttentionRides();
      setRides(unwrapList(res));
    } catch (e) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <Stack
      spacing={2}
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        height: "calc(100vh - 80px)",
        overflowY: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => navigate("/dispatch")} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
              Needs Attention
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Rides the auto-reassign loop couldn't place — manual reassignment or cancellation required.
            </Typography>
          </Box>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading && rides.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rides.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              All clear. No rides need manual intervention.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        rides.map((r) => (
          <Card key={r.rideId} variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${STATUS_COLORS[r.status] || "#94a3b8"}` }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[r.status] || r.status}
                      sx={{
                        fontWeight: 700,
                        backgroundColor: STATUS_COLORS[r.status] || "#94a3b8",
                        color: "#fff",
                        height: 22,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {fmtAge(r.lastStatusChangeMs)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {r.customerCompanyName || "(no company)"} · Start {fmtStart(r.plannedStartTimeMs)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Pickup: {r.pickupAddress || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Drop-off: {r.dropoffAddress || "—"}
                  </Typography>
                  {r.lastDriverName ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Last driver: {r.lastDriverName}
                    </Typography>
                  ) : null}
                </Box>
                <Box>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => navigate(`/rides/${r.rideId}`)}
                    sx={{ backgroundColor: "#1F4E78" }}
                  >
                    Reassign
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))
      )}
    </Stack>
  );
};

export default NeedsAttentionPage;
